import { formatDocumentName } from "encamp-shared/src/documents";
import { DocumentType } from "generated-graphql/graphql";
import { useState, useMemo } from "react";

export interface FilenameStandardizationOptions {
  originalFilename?: string;
  documentType?: DocumentType;
  facilityName?: string;
  date?: Date;
  /** Whether this is an edit operation (hides checkbox) */
  isEdit?: boolean;
}

export interface UseFilenameStandardizationReturn {
  /** The standardized filename based on current settings */
  standardizedFilename: string | undefined;
  /** Whether to keep the original filename */
  keepOriginalFilename: boolean;
  /** Function to toggle the keep original filename checkbox */
  setKeepOriginalFilename: (keep: boolean) => void;
  /** Whether the checkbox should be shown */
  shouldShowCheckbox: boolean;
}

/**
 * Hook for managing filename standardization across upload components.
 * Provides logic for standardizing filenames and managing the "Keep original filename" option.
 */
export function useFilenameStandardization(
  options: FilenameStandardizationOptions
): UseFilenameStandardizationReturn {
  const [keepOriginalFilename, setKeepOriginalFilename] = useState(false);

  const {
    originalFilename,
    documentType,
    facilityName,
    date = new Date(),
    isEdit = false,
  } = options;

  // Check if this document type should be excluded from standardization
  const shouldExcludeFromStandardization = useMemo(() => {
    return !documentType || documentType === DocumentType.SafetyDataSheet;
  }, [documentType]);

  const shouldShowCheckbox = useMemo(() => {
    return !isEdit && !shouldExcludeFromStandardization;
  }, [isEdit, shouldExcludeFromStandardization]);

  const shouldStandardize = useMemo(() => {
    return !shouldExcludeFromStandardization && !keepOriginalFilename;
  }, [shouldExcludeFromStandardization, keepOriginalFilename]);

  const extension = useMemo(() => {
    return originalFilename?.split(".").pop();
  }, [originalFilename]);

  // Generate filename
  const standardizedFilename = useMemo(() => {
    if (!shouldStandardize || !documentType) {
      return originalFilename;
    }

    return formatDocumentName({
      document: {
        documentType,
        fileExtension: extension || "",
      },
      facilityName: facilityName || "",
      date,
    });
  }, [documentType, facilityName, date, shouldStandardize, originalFilename, extension]);

  return {
    standardizedFilename,
    keepOriginalFilename,
    setKeepOriginalFilename,
    shouldShowCheckbox,
  };
}
